import { BrowserRouter as Router, Route, Switch } from "react-router-dom";

import { Grid } from "@material-ui/core";
import { ErrorBoundary } from "@sentry/react";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { v4 } from "uuid";
import { PATHNAMES } from "../../constants/pathnames";
import { useAuth } from "../../providers/Auth";
import { FeatureFlagsProvider } from "../../providers/LaunchDarklyProvider";
import { authenticationStore } from "../../stores/authentication-store";
import ErrorFallback from "../App/ErrorFallback";
import { IdleHandler } from "../App/IdleHandler";
import Feedback from "../Feedback";
import { Journey } from "../Journey/Journey";
import Loading from "../LoadingLayout/Loading";
import LoadingLayout from "../LoadingLayout/LoadingLayout";
import Monitoring from "../Monitoring/Monitoring";
import { Profile } from "../Profile/Profile";
import { Results } from "../Results/Results";
import IntakeForm from "../Scheduling/Location/IntakeForm";
import UnknownRoute from "../UnknownRoute/UnknownRoute";
import { Wrapper } from "../Wrapper/Wrapper";
import { WrapperPublic } from "../Wrapper/WrapperPublic";
import { HardRedirect } from "../App/LinkWithPathBasedReload";

const PrivateRoute = ({
  excludeWrapper,
  component: Component,
  exact,
  strict,
  path,
  allowedUserTypes,
  ...rest
}) => {
  const { user, isAuthenticated } = useAuth();

  return (
    <Route
      exact={exact}
      strict={strict}
      path={path}
      render={(props) => {
        if (isAuthenticated) {
          if (user?.accountRegistrationState?.registrationComplete) {
            return excludeWrapper ? (
              <Component {...props} {...rest} />
            ) : (
              <Wrapper>
                <Component {...props} {...rest} />
              </Wrapper>
            );
          } else {
            return (
              <HardRedirect
                pathname={PATHNAMES.DASHBOARD} // let portal handle it
                state={{ from: props.location }}
              />
            );
          }
        } else {
          return (
            <HardRedirect
              pathname={
                authenticationStore.redirectLogout
                  ? authenticationStore.redirectLogout
                  : PATHNAMES.LOGIN
              }
              state={{ from: props.location }}
              search={`redirect_auth=${props.location.pathname}${props.location.search}`}
            />
          );
        }
      }}
    />
  );
};

export const Navigator = observer(() => {
  return (
    <Router>
      <ErrorBoundary
        fallback={(p) => (
          <Grid container style={{ width: "100vw", height: "100vh" }}>
            <ErrorFallback {...p} />
          </Grid>
        )}
      >
        <LoadingLayout>
          <IdleHandler />
          <Switch>
            <PrivateRoute
              path={PATHNAMES.MONITORING}
              component={() => <Monitoring />}
            />
            <PrivateRoute
              path={[PATHNAMES.MY_PROFILE, PATHNAMES.MY_PROFILE_NYC]}
              component={() => <Profile />}
            />
            <PrivateRoute
              path={PATHNAMES.JOURNEY}
              component={() => <Journey />}
            />
            <Route
              path={PATHNAMES.JOURNEY_DEMO}
              component={() => (
                <WrapperPublic fullBackground>
                  <Journey publicView />
                </WrapperPublic>
              )}
            />
            <PrivateRoute
              path={PATHNAMES.RESULTS}
              component={() => <Results />}
            />
            <Route path={PATHNAMES.FEEDBACK} component={() => <Feedback />} />
            <PrivateRoute
              path={[PATHNAMES.INTAKE, PATHNAMES.INTAKE_NYC]}
              component={() => <IntakeForm />}
            />
            <Route path="*">
              <UnknownRoute />
            </Route>
          </Switch>
        </LoadingLayout>
      </ErrorBoundary>
    </Router>
  );
});

export const LDRouter = () => {
  const ldUuidStorageKey = "ld-device-identifier";
  const [uuidExists, setUuidExists] = useState(false);
  const [uuidProp, setUuidProp] = useState({});

  useEffect(() => {
    const ldUuid = localStorage.getItem(ldUuidStorageKey);
    if (!ldUuid) {
      localStorage.setItem(ldUuidStorageKey, v4());
      setUuidExists(true);
    } else {
      setUuidExists(true);
    }
    if (uuidExists) {
      setUuidProp({ user: ldUuid });
    }
  }, [uuidExists]);

  return (
    <FeatureFlagsProvider
      user={uuidProp.user ?? null}
      apiKey={import.meta.env.VITE_LAUNCHDARKLY_CLIENT_KEY}
      loadingFallback={<Loading />}
    >
      <Navigator />
    </FeatureFlagsProvider>
  );
};
